import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react"
import { getAdminMe } from "@/api/adminAuthApi"

type AdminUser = {
    memberId: number
    loginId: string
    memberName: string
    role: string
}

type AdminAuthContextType = {
    adminUser: AdminUser | null
    isAdminLoggedIn: boolean
    isLoading: boolean
    refreshAdminAuth: () => Promise<void>
    clearAdminAuth: () => void
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined)

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
    const [adminUser, setAdminUser] = useState<AdminUser | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    const refreshAdminAuth = useCallback(async () => {
        try {
            setIsLoading(true)
            const response = await getAdminMe()

            if (response.success && response.data) {
                setAdminUser(response.data)
            } else {
                setAdminUser(null)
            }
        } catch (error) {
            console.error(error)
            setAdminUser(null)
        } finally {
            setIsLoading(false)
        }
    }, [])

    const clearAdminAuth = useCallback(() => {
        setAdminUser(null)
    }, [])

    useEffect(() => {
        refreshAdminAuth()
    }, [refreshAdminAuth])

    const value = useMemo(
        () => ({
            adminUser,
            isAdminLoggedIn: !!adminUser,
            isLoading,
            refreshAdminAuth,
            clearAdminAuth,
        }),
        [adminUser, isLoading, refreshAdminAuth, clearAdminAuth],
    )

    return (
        <AdminAuthContext.Provider value={value}>
            {children}
        </AdminAuthContext.Provider>
    )
}

export function useAdminAuth() {
    const context = useContext(AdminAuthContext)

    if (!context) {
        throw new Error("useAdminAuth must be used within AdminAuthProvider")
    }

    return context
}